import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, type SupplierMargin } from "@/lib/api";
import { cn } from "@/lib/utils";

interface SupplierMarginsProps {
  marketplaceId: string;
  marketplaceLabel: string;
}

function isValidMargin(raw: string): boolean {
  const trimmed = raw.trim();
  if (!trimmed) return true; // vuoto = eredita, e' un valore legittimo
  if (!/^\d{1,2}([.,]\d{1,2})?$/.test(trimmed)) return false;
  const n = Number(trimmed.replace(",", "."));
  return !Number.isNaN(n) && n >= 0 && n <= 60;
}

/** Confronta il draft col persistito: "" e null sono lo stesso stato (eredita). */
function changed(draft: string, persisted: string | null): boolean {
  return draft.trim().replace(",", ".") !== (persisted ?? "");
}

/**
 * Margine obiettivo per fornitore su un canale price-setter.
 *
 * Esiste perche' il gate reale e' fornitore x marketplace, ma le due dimensioni
 * vivevano in liste separate da incrociare a memoria. Qui stanno insieme, sotto
 * il canale a cui appartengono, chiuse finche' non servono.
 *
 * Campo vuoto = eredita dal canale: il placeholder mostra sempre il valore che
 * verrebbe applicato, cosi' "eredita" non e' mai un'incognita.
 */
export function SupplierMargins({
  marketplaceId,
  marketplaceLabel,
}: SupplierMarginsProps): JSX.Element {
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [open, setOpen] = useState(false);

  const margins = useQuery<SupplierMargin[]>({
    queryKey: ["supplier-margins", marketplaceId],
    queryFn: () => api.supplierMargins(marketplaceId),
    enabled: open, // niente fetch finche' il pannello resta chiuso
  });

  const save = useMutation({
    mutationFn: ({ slug, value }: { slug: string; value: string | null }) =>
      api.setSupplierMargin(marketplaceId, slug, value),
    onSuccess: (result) => {
      qc.setQueryData<SupplierMargin[]>(["supplier-margins", marketplaceId], (current) =>
        (current ?? []).map((row) => (row.slug === result.slug ? result : row)),
      );
      setDrafts((current) => {
        const next = { ...current };
        delete next[result.slug];
        return next;
      });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["supplier-margins", marketplaceId] });
    },
  });

  const rows = margins.data ?? [];
  const overrides = rows.filter((row) => row.margin_pct !== null).length;

  return (
    <details
      className="mt-3 border-t border-line pt-3"
      onToggle={(event) => setOpen((event.target as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer text-label font-semibold text-ink-3">
        Margine per fornitore
        {overrides > 0 && (
          <span className="text-accent-ink ml-2 normal-case tracking-normal">
            {overrides} {overrides === 1 ? "override" : "override"}
          </span>
        )}
      </summary>

      {margins.isLoading && <p className="mt-2 font-mono text-label text-ink-3">Caricamento…</p>}
      {margins.isError && (
        <p className="mt-2 font-mono text-label text-destructive">
          Errore caricamento margini:{" "}
          {margins.error instanceof Error ? margins.error.message : "richiesta fallita"}
        </p>
      )}

      {rows.length > 0 && (
        <ul className="mt-2 space-y-1">
          {rows.map((row) => {
            const draft = drafts[row.slug] ?? row.margin_pct ?? "";
            const valid = isValidMargin(draft);
            const dirty = changed(draft, row.margin_pct);
            const pending = save.isPending && save.variables?.slug === row.slug;
            const inputId = `margine-${marketplaceId}-${row.slug}`;
            const failed = save.isError && save.variables?.slug === row.slug ? save.error : null;

            return (
              <li key={row.slug}>
                <div className="grid items-center gap-3 md:grid-cols-[minmax(220px,1fr)_140px_140px_140px_120px_120px]">
                  <label htmlFor={inputId} className="min-w-0 truncate text-sm text-ink-2">
                    {row.label}
                  </label>
                  <div
                    className={cn(
                      "control-shell h-8 px-2 md:col-start-3",
                      dirty && "border-key-press bg-key/15",
                      !valid && "border-destructive/50 bg-destructive/5",
                    )}
                  >
                    <input
                      id={inputId}
                      value={draft}
                      placeholder={row.effective_pct}
                      aria-label={`Margine ${row.label} su ${marketplaceLabel}`}
                      aria-invalid={!valid}
                      inputMode="decimal"
                      onChange={(event) =>
                        setDrafts((current) => ({
                          ...current,
                          [row.slug]: event.target.value,
                        }))
                      }
                      className="w-full bg-transparent font-mono text-xs tabular-nums outline-none"
                    />
                    <span className="font-mono text-label text-ink-3">%</span>
                  </div>
                  <span className="font-mono text-label text-ink-3">
                    {row.margin_pct === null ? "eredita" : "override"}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm justify-center md:col-start-6"
                    disabled={!dirty || !valid || pending}
                    onClick={() =>
                      save.mutate({
                        slug: row.slug,
                        value: draft.trim() ? draft.trim().replace(",", ".") : null,
                      })
                    }
                  >
                    {pending ? "…" : "Salva"}
                    <span className="sr-only"> margine {row.label}</span>
                  </button>
                </div>
                {!valid && (
                  <p className="mt-1 font-mono text-label text-destructive">
                    Numero tra 0 e 60, oppure vuoto per ereditare dal canale.
                  </p>
                )}
                {failed && (
                  <p
                    role="alert"
                    aria-live="assertive"
                    className="mt-1 font-mono text-label text-destructive"
                  >
                    Non salvato: {failed instanceof Error ? failed.message : "richiesta fallita"}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {rows.length > 0 && (
        <p className="mt-2 max-w-2xl font-mono text-label text-ink-3">
          Vuoto = eredita dal canale (il grigio nel campo e&apos; il valore applicato). Un prodotto
          senza fornitore riconosciuto usa sempre il margine di canale.
        </p>
      )}
    </details>
  );
}
